import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Avatar from 'components/field/Avatar';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import ConfirmationModal from 'components/global/ConfirmationModal';
import FormHeader from 'components/global/FormHeader';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import OTPModal from 'pages/OTP/OTPModal';

import { COMPANY, PROFILE } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';
import { setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES } from 'utils/constants';
import PATHS from 'utils/paths';
import { validateIBAN, validateIBANFormat } from 'utils/utils';

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faEdit, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import useBillingDetailsValidation from '../Payment/hooks/useBillingValidation';
import AccountRemovalModal from './AccountRemovalModal';
import BillingInfo from './BillingInfo';
import * as S from './GeneralSettings.styled';
import InvoiceTypeFormForSettings from './InvoiceTypeFormForSettings';

const SectionTitle = ({ title, description, hasPadding = true }) => (
	<S.SectionTitleContainer className={!hasPadding && 'pb-0'}>
		<Typography variant="p2" fontWeight="500">
			{title}
		</Typography>
		<Typography color="var(--content-2)" noWrap>
			{description}
		</Typography>
	</S.SectionTitleContainer>
);

const GeneralSettings = () => {
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const navigate = useNavigate();
	const { showSuccess, showError, showWarning } = useAlert();
	const { updateOnboarding } = useAppTracking();
	const {
		mobileVerified,
		companyAdminVerified,
		phone,
		planName,
		fullName,
		email,
		firstName,
		lastName,
		generalSettings: {
			id,
			ClientName,
			ClientEmail,
			CompanyLogo,
			BankName,
			BankHolderName,
			BankAccountNumber,
			BankSwift,
			BankIban,
			banks,
			fullName: invoiceFullName
		}
	} = useSelector((state) => state?.user);

	const isEnterprise = planName === 'EnterprisePackage';
	const isTRUser = useMemo(() => isAuthorized('/company-settings/TRInvoiceType'), [isAuthorized]);
	const [disabledBankInformation, setDisabledBankInformation] = useState(true);

	const token = useRef(null);
	const { billingDetailsSchema, billingDefaults } = useBillingDetailsValidation();

	const formMethods = useForm({
		resolver: yupResolver(billingDetailsSchema),
		defaultValues: {
			...billingDefaults,
			ClientName,
			ClientEmail,
			CompanyLogo,
			BankName,
			BankHolderName,
			bankAccountNumber: BankAccountNumber,
			bankSwift: BankSwift,
			BankIban,
			fullName: invoiceFullName
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		setError,
		clearErrors,
		getValues,
		control,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading, defaultValues }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (data, updateBankInfo = true) => {
		try {
			const mutableData = JSON.parse(JSON.stringify(data));
			const req = {
				...mutableData,
				ID: id,
				VATNumber: mutableData.invoiceType === ACCOUNT_TYPES.COMPANY ? mutableData.VATNumber : null,
				CRNumber: mutableData.invoiceType === ACCOUNT_TYPES.COMPANY ? mutableData.CRNumber : null,
				fullName: mutableData.invoiceType === ACCOUNT_TYPES.PERSONAL ? mutableData.fullName : null,
				idNumber: mutableData.invoiceType === ACCOUNT_TYPES.PERSONAL ? mutableData.idNumber : null,
				taxOffice: mutableData.invoiceType === ACCOUNT_TYPES.COMPANY ? mutableData.taxOffice : null,
				CRDocumentURL: mutableData.invoiceType === ACCOUNT_TYPES.COMPANY ? mutableData.CRDocumentURL : null,
				freelanceDocument:
					mutableData.invoiceType === ACCOUNT_TYPES.FREELANCER ? mutableData.freelanceDocument?.path : null,
				civilID:
					mutableData.invoiceType === ACCOUNT_TYPES.FREELANCER ||
					mutableData.invoiceType === ACCOUNT_TYPES.COMPANY
						? mutableData.civilID?.path
						: null,
				CRDocument: mutableData.invoiceType === ACCOUNT_TYPES.COMPANY ? mutableData.CRDocument : null
			};
			const response = await axiosPrivate.post(COMPANY.UPDATE_GENERAL_SETTINGS, req);
			const { status } = response;
			if (status === 200) {
				if (updateBankInfo) {
					const dontUpdatedKeys = [
						'BankName',
						'BankHolderName',
						'bankAccountNumber',
						'bankSwift',
						'BankIban'
					];
					dontUpdatedKeys.forEach((key) => {
						req[key] = defaultValues[key] || null;
					});
				}

				dispatch(
					setUserGeneralSettings({
						...req,
						FLDocStatus: req.invoiceType === ACCOUNT_TYPES.FREELANCER ? 'UNVERIFIED' : '',
						CRDocStatus: req.invoiceType === ACCOUNT_TYPES.COMPANY ? 'UNVERIFIED' : ''
					})
				);
				reset({
					...req,
					freelanceDocument:
						mutableData.invoiceType === ACCOUNT_TYPES.FREELANCER
							? {
									path: mutableData.freelanceDocument?.path ?? null,
									name: 'freelanceDocument'
								}
							: null,
					civilID:
						mutableData.invoiceType === ACCOUNT_TYPES.FREELANCER ||
						mutableData.invoiceType === ACCOUNT_TYPES.COMPANY
							? {
									path: mutableData.civilID?.path ?? null,
									name: 'civilID'
								}
							: null
				});
				if (mutableData.BankIban && mutableData.BankHolderName) {
					updateOnboarding(['bankInfoCheck']);
				}

				showSuccess();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};
	const handleVerifyOTP = useCallback(
		async (modal, code) => {
			setLoading(true);

			await axiosPrivate
				.post(COMPANY.UPDATE_BANK_ACCOUNT_INFORMATION, {
					ID: id,
					BankIban: getValues('BankIban'),
					BankHolderName: getValues('BankHolderName'),
					BankName: getValues('BankName'),
					bankId: getValues('BankID'),
					bankAccountNumber: getValues('bankAccountNumber') || '',
					bankSwift: getValues('bankSwift') || '',
					code,
					token: token.current
				})
				.then((response) => {
					if (response.status === 200) {
						setDisabledBankInformation(true);
						showSuccess({
							message: 'changeBankInformation.successMessage'
						});
						const updatedBankInfo = {
							BankIban: getValues('BankIban'),
							BankHolderName: getValues('BankHolderName'),
							BankName: getValues('BankName'),
							bankAccountNumber: getValues('bankAccountNumber'),
							bankSwift: getValues('bankSwift')
						};
						reset({
							...getValues(),
							...updatedBankInfo
						});
						dispatch(setUserGeneralSettings(updatedBankInfo));
						modal.remove();
						if (watch('kycRegisterStatus') !== 'active') {
							onSubmit(watch(), false);
						}
					} else {
						AppLogger.error(response);
						showError();
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					if (error?.response?.data?.code === 'error.B011') {
						showError({
							message: t('error.B011')
						});
					} else {
						showError();
					}
				});
		},
		[showError, getValues, token.current, showSuccess, id, axiosPrivate]
	);

	const handleSendCode = useCallback(async () => {
		dispatch(setLoading(true));
		clearErrors('BankIban');
		clearErrors('BankHolderName');
		clearErrors('bankAccountNumber');
		clearErrors('bankSwift');

		// Validate IBAN
		const ibanControl = watch('BankIban');

		if (!validateIBANFormat(ibanControl)) {
			setError('BankIban', {
				type: 'manual',
				message: t('error.notValidIBANFormat')
			});

			dispatch(setLoading(false));
			return;
		} else if (!validateIBAN(ibanControl)) {
			setError('BankIban', {
				type: 'manual',
				message: t('error.notValidIBAN')
			});

			dispatch(setLoading(false));
			return;
		}

		// Validate bank holder name
		const bankHolderName = watch('BankHolderName');

		if (bankHolderName.length > 35) {
			setError('BankHolderName', {
				type: 'manual',
				message: t('error.max35CharactersBankHolderName')
			});

			dispatch(setLoading(false));
			return;
		}

		await axiosPrivate
			.post(PROFILE.REQUEST_MOBILE_OWNERSHIP_VERIFICATION, {
				otpType: 'bankInfo'
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess({ message: 'mobileNumber.codeSended' });
					if (response.data?.token) {
						token.current = response.data.token;
						NiceModal.show(OTPModal, {
							data: phone,
							sendOTPCode: handleSendCode,
							verifyOTP: handleVerifyOTP,
							isMail: false
						});
						return response.data.token;
					} else {
						showError({ message: 'mobileNumber.cantVerified' });
						AppLogger.error('General Settings: Token was not come from the server');
						return false;
					}
				} else {
					showError({ message: 'mobileNumber.cantVerified' });
					AppLogger.error(`General Settings: Error while sending verify code: ${response}`);
					return false;
				}
			})
			.catch((error) => {
				if (error?.response?.data?.code === 'error.B008') {
					showError({
						header: 'error.B008',
						message: 'errorMsg.telephoneNumberValidation'
					});
				} else if (error?.response?.data?.code === 'error.B009') {
					showError({
						message: t('error.B009', {
							minutes: 2
						})
					});
				} else if (error?.response?.data?.code === 'error.EM02') {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				} else {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
					AppLogger.error(`General Settings: Error while sending verify code: ${error}`);
				}
				return false;
			});

		dispatch(setLoading(false));
	}, [showError, showSuccess, t, phone, handleVerifyOTP, setValue, dispatch, axiosPrivate]);

	const handleChangeBankInfo = useCallback(() => {
		if (!mobileVerified) {
			showWarning({
				header: 'changeBankInformation.verifyMobileNumberHeader',
				message: 'changeBankInformation.verifyMobileNumberMessage'
			});
			navigate(PATHS.USER_SETTINGS);
		} else if (!companyAdminVerified) {
			showError({
				header: 'changeBankInformation.verifyCompanyAdminMobileHeader',
				message: 'changeBankInformation.verifyCompanyAdminMobileMessage'
			});
		} else {
			NiceModal.show(ConfirmationModal, {
				title: 'changeBankInformation.confirmationHeader',
				subTitle: 'changeBankInformation.confirmationMessage',
				confirmLabel: 'approve',
				rejectLabel: 'cancel',
				yesAction: () => {
					setDisabledBankInformation(false);
				}
			});
		}
	}, [mobileVerified, companyAdminVerified, navigate, showWarning, showError, setDisabledBankInformation]);

	return (
		<S.Container>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="companySettings" />
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						onClick={handleSubmit(onSubmit, AppLogger.log)}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						className="saveButton"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>

			<S.StyledForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					<S.FlexBox $alignItems="center">
						<Avatar
							defaultImage={
								'https://storage.googleapis.com/tryoto-public/public/assets/images/company-default.png'
							}
							value={useWatch({
								control,
								name: 'CompanyLogo'
							})}
							setValue={(value) => {
								setValue('CompanyLogo', value, { shouldValidate: true, shouldDirty: true });
							}}
							isLoading={isLoading}
							maxFileSize={1000000}
							size="large"
						/>
						<S.FlexBox $flexDirection="column" style={{ display: 'grid' }}>
							<Typography fontWeight="500" fontSize="1.85rem">
								{lastName ? `${firstName} ${lastName}` : fullName}
							</Typography>
							<Typography fontWeight="400" fontSize="1.35rem" color="var(--content-3)">
								{email}
							</Typography>
						</S.FlexBox>
					</S.FlexBox>
					<S.GeneralSettingsContainer>
						<S.FormContainer>
							<FormItem label="clientName" isLoading={isLoading} required>
								<StringInput name="ClientName" disabled />
							</FormItem>

							<FormItem label="clientEmail" isLoading={isLoading} required>
								<StringInput name="ClientEmail" disabled />
							</FormItem>
						</S.FormContainer>
					</S.GeneralSettingsContainer>
				</S.StyledCard>

				<S.StyledCard className="settingsMaxWidth" fullWidth>
					<SectionTitle
						title="accountTypeAndBillingDetails"
						description="accountTypeAndBillingDetails.description"
					/>

					<S.GeneralSettingsContainer>
						<Authorization path="/company-settings/payment-details">
							<InvoiceTypeFormForSettings />
						</Authorization>
					</S.GeneralSettingsContainer>
				</S.StyledCard>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					<SectionTitle title="billingAddress" description="billingAddress.description" />

					<BillingInfo isLoading={isLoading} />
				</S.StyledCard>
				<Authorization path="/company-settings/payment-details">
					<S.StyledCard className="settingsMaxWidth" fullWidth>
						<S.SectionRow>
							<SectionTitle title="bankDetails" description="bankDetails.description" />
							<div>
								<FormItem col="1" isLoading={isLoading} className="base-form-item py-0 px-0">
									{disabledBankInformation ? (
										<Button
											label="changeBankInformation"
											icon={faEdit}
											variant="outline"
											onClick={handleChangeBankInfo}
										/>
									) : (
										<Button
											label="verifyBankInformation"
											onClick={handleSendCode}
											disabled={
												watch('BankIban') === null ||
												watch('BankHolderName') === null ||
												watch('BankName') === null ||
												(!isTRUser &&
													(watch('bankAccountNumber') === null ||
														watch('bankSwift') === null))
											}
											disabledTooltip={
												watch('BankIban') === null ||
												watch('BankHolderName') === null ||
												watch('bankAccountNumber') === null ||
												watch('bankSwift') === null ||
												watch('BankName') === null
													? 'formInfo.fillAllBankFields'
													: undefined
											}
										/>
									)}
								</FormItem>
							</div>
						</S.SectionRow>
						{!isEnterprise && (
							<FormItem className="base-form-item px-0">
								<Alert content={`codTransferSchedule.${planName}`} showIcon />
							</FormItem>
						)}

						<S.FormContainer>
							<FormItem
								label="bankName"
								isLoading={isLoading}
								toolTip="bankName.descriptionWithOTP"
								required={!disabledBankInformation}>
								<EnumSelect
									name="BankName"
									data={banks?.map((item) => item.name)}
									setValue={(value) => {
										setValue('BankName', value);
										setValue('BankID', banks.find((item) => item.name === value).id);
									}}
									placeholder="selectOne"
									value={useWatch({
										control,
										name: 'BankName'
									})}
									valueRemovable={false}
									disabled={disabledBankInformation || !companyAdminVerified || !mobileVerified}
								/>
							</FormItem>

							<FormItem label="bankHolderName" isLoading={isLoading} required={!disabledBankInformation}>
								<StringInput
									name="BankHolderName"
									disabled={disabledBankInformation || !companyAdminVerified || !mobileVerified}
								/>
							</FormItem>

							<FormItem
								label="bankAccountNumber"
								isLoading={isLoading}
								toolTip="bankAccountNumber.message"
								required={!isTRUser && !disabledBankInformation}>
								<StringInput
									name="bankAccountNumber"
									disabled={disabledBankInformation || !companyAdminVerified || !mobileVerified}
								/>
							</FormItem>
							<FormItem
								label="bankSwift"
								isLoading={isLoading}
								toolTip="bankSwift.message"
								required={!isTRUser && !disabledBankInformation}>
								<StringInput
									name="bankSwift"
									disabled={disabledBankInformation || !companyAdminVerified || !mobileVerified}
								/>
							</FormItem>
							<FormItem
								label="bankIban"
								isLoading={isLoading}
								toolTip="bankIban.message"
								required={!disabledBankInformation}>
								<StringInput
									name="BankIban"
									disabled={disabledBankInformation || !companyAdminVerified || !mobileVerified}
								/>
							</FormItem>
						</S.FormContainer>
					</S.StyledCard>
				</Authorization>

				<Authorization path="/company-settings/account-removal">
					<S.StyledCard className="settingsMaxWidth" fullWidth>
						<S.SectionRow>
							<SectionTitle title="accountRemoval" description="accountRemoval.description" />
							<div>
								<FormItem isLoading={isLoading} className="base-form-item px-0">
									<Button
										icon={faTrashCan}
										variant="outline"
										label="deleteAccount"
										className="delete-account"
										onClick={() => {
											NiceModal.show(AccountRemovalModal);
										}}
									/>
								</FormItem>
							</div>
						</S.SectionRow>
					</S.StyledCard>
				</Authorization>
			</S.StyledForm>

			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					label="save"
					variant="blue"
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					className="saveButton"
				/>
			</FormFooter>
		</S.Container>
	);
};

export default GeneralSettings;
